import { useMetadataUser } from 'contexts/metadata-user-context'
import { ReactNode, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getCookie, removeCookie } from 'utils/functions-cookie'

type Props = {
  children: ReactNode
  redirectTo?: string
}

const PUBLIC_ROUTES = new Set([
  '/login',
  '/signup',
  '/forgot-password',
  '/',
  'form-forgot-password',
])

const tokenExpiration = 1000 * 60 * 60 // 1 hora

export const PrivateRoute = ({ children, redirectTo }: Props) => {
  const isAuthenticated = !!getCookie('auth_token')
  const refreshToken = !!getCookie('refresh_token')
  const { profile } = useMetadataUser()

  const navigation = useNavigate()
  const pathname = useLocation().pathname
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Se não está autenticado ou falta o refresh token
    if (!isAuthenticated || (isAuthenticated && !refreshToken)) {
      removeCookie('auth_token')
      if (!PUBLIC_ROUTES.has(pathname)) {
        window.location.href = `${redirectTo ?? '/login'}`
      }
      return
    }

    // Se autenticado e na rota pública, redireciona para /home
    if (isAuthenticated && refreshToken && PUBLIC_ROUTES.has(pathname)) {
      navigation('/home')
      return
    }

    // Configura o temporizador de logout apenas uma vez
    if (!logoutTimerRef.current) {
      logoutTimerRef.current = setTimeout(() => {
        removeCookie('auth_token')
        removeCookie('refresh_token')
        window.location.href = '/login'
      }, tokenExpiration)
    }

    // Limpa o temporizador ao desmontar o componente
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
        logoutTimerRef.current = null
      }
    }
  }, [isAuthenticated, navigation, redirectTo, refreshToken, pathname, profile])

  if (
    !isAuthenticated ||
    (isAuthenticated && !refreshToken) ||
    (isAuthenticated && refreshToken && PUBLIC_ROUTES.has(pathname))
  ) {
    return null
  }

  return <>{children}</>
}
